import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import './Notification.css';

export type NotificationType = 'success' | 'error' | 'info';

interface NotificationProps {
  message: string;
  type?: NotificationType;
  onClose: () => void;
  autoHideDuration?: number;
}

const Notification: React.FC<NotificationProps> = ({
  message,
  type = 'success',
  onClose,
  autoHideDuration = 6000,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const onCloseRef = useRef(onClose);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (closeTimerRef.current) {
      return;
    }

    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => onCloseRef.current(), 180);
  }, []);

  useEffect(() => {
    if (autoHideDuration > 0) {
      const timer = setTimeout(requestClose, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [autoHideDuration, requestClose]);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
  }, []);

  const Icon = type === 'success' ? CircleCheck : type === 'error' ? CircleAlert : Info;
  const notificationStyle = {
    '--notification-duration': `${autoHideDuration}ms`,
  } as CSSProperties;

  return (
    <div
      className={`notification notification--${type}${isClosing ? ' notification--closing' : ''}`}
      role={type === 'error' ? 'alert' : 'status'}
      aria-atomic="true"
      style={notificationStyle}
    >
      <span className="notification-accent" aria-hidden="true" />
      <div className="notification-icon" aria-hidden="true">
        <Icon size={20} strokeWidth={2.2} />
      </div>
      <div className="notification-message">{message}</div>
      <button
        type="button"
        className="notification-close"
        onClick={requestClose}
        aria-label="Închide notificarea"
        title="Închide"
      >
        <X size={17} strokeWidth={2.2} aria-hidden="true" />
      </button>
      {autoHideDuration > 0 && <span className="notification-progress" aria-hidden="true" />}
    </div>
  );
};

export default Notification;
